import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createAppThunk, type IRes } from "store/modules/common";
import Http from "commons/Http";
import i18n from "i18n";

// 이름 설정
const name = "signup";

export interface ISignup {
  confirm: {
    id?: string;
    num?: string;
    token?: string;
    verifyFlag: boolean;
    sendNumberFlag: boolean;
    isLoading?: boolean;
  };
}

// 핸드폰,이메일 인증번호 요청
export const apiPsendAuthNumber = createAppThunk<{ id: string }, { type: string; value: string }>(
  `${name}/apiPsendAuthNumber`,
  async (param, thunkApi) => {
    const res = await Http.post<IRes<{ id: string }>>(
      `auth/${param.type}`,
      { value: param.value },
      { headers: { "Accept-Language": i18n.language } },
    );
    if (res.data.content) {
      thunkApi.dispatch(rdxSetConfirmIdValue({ id: res.data.content.id, phone: param.value }));
    }
    return res.data;
  },
);

// 핸드폰,이메일 인증번호 확인 api
export const apiPConfirmNumCheck = createAppThunk<{ result: boolean; token: string }, { type: string; value?: string }>(
  `${name}/apiPConfirmNumCheck`,
  async (param, thunkApi) => {
    const res = await Http.post<IRes<{ result: boolean; token: string }>>(`auth/${param.type}/verify`, {
      id: thunkApi.getState().signup.confirm.id,
      code: param.value,
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetConfirmToken(res.data.content.token));
    }
    return res.data;
  },
);

// 이메일 회원가입
export const apiPemailSignUp = createAppThunk(`${name}/apiPemailSignUp`, async (_, thunkApi) => {
  const res = await Http.post("user/signUp", {
    auth_type: i18n.language === "en" ? "EMAIL" : "PHONE",
    token: thunkApi.getState().signup.confirm.token,
    email: thunkApi.getState().ui.inputText?.["signupEmail"]?.trim(),
    password: thunkApi.getState().ui.inputText?.["signupPasswd"]?.trim(),
    name: thunkApi.getState().ui.inputText?.["signupName"]?.trim(),
    nation: i18n.language === "en" ? "US" : "KR",
  });

  return res.data;
});

// 카카오 회원가입
export const apiPkakaoSignUp = createAppThunk(`${name}/apiPkakaoSignUp`, async (_, thunkApi) => {
  const res = await Http.post("user/signUp/kakao", {
    token: thunkApi.getState().auth.kakao.token,
    phone_token: thunkApi.getState().signup.confirm.token,
    name: thunkApi.getState().ui.inputText?.["signupName"]?.trim(),
  });

  return res.data;
});

const signupSlice = createSlice({
  name,
  initialState: {
    confirm: {
      verifyFlag: false,
      sendNumberFlag: false,
    },
  } as ISignup,
  reducers: {
    //인증번호 요청할때 오는 응닶값 id 저장
    rdxSetConfirmIdValue(state: ISignup, action: PayloadAction<{ id: string; phone: string }>) {
      state.confirm.id = action.payload.id;
      state.confirm.num = action.payload.phone;
    },
    //핸드폰,이메일 인증 후 토큰 값 저장
    rdxSetConfirmToken(state: ISignup, action: PayloadAction<string>) {
      state.confirm.token = action.payload;
    },

    //핸드폰,이메일 인증번호 요청 클릭 했는지 안했는지 여부
    rdxSetConfirmSendNumber(state: ISignup, action: PayloadAction<boolean>) {
      state.confirm.sendNumberFlag = action.payload;
    },
    //핸드폰,이메일 인증 했는지 안했는지 여부
    rdxSetConfirmVerify(state: ISignup, action: PayloadAction<boolean>) {
      state.confirm.verifyFlag = action.payload;
    },

    //핸드폰 인증 정보 초기화
    rdxResetConfirmInfo(state: ISignup) {
      delete state.confirm.id;
      delete state.confirm.token;
      delete state.confirm.num;
      delete state.confirm.isLoading;
      state.confirm.sendNumberFlag = false;
      state.confirm.verifyFlag = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(apiPsendAuthNumber.pending, state => {
      state.confirm.isLoading = true;
    });
    builder.addCase(apiPsendAuthNumber.fulfilled, state => {
      state.confirm.isLoading = false;
    });
  },
});

export const {
  rdxSetConfirmIdValue,
  rdxSetConfirmToken,
  rdxSetConfirmSendNumber,
  rdxSetConfirmVerify,
  rdxResetConfirmInfo,
} = signupSlice.actions;

export default signupSlice.reducer;
